import type {
    CreateMyWishlistMutationVariables,
    DeleteMyWishlistMutationVariables,
    EccoGetMyActiveWishlistQuery,
    EccoGetMyActiveWishlistQueryVariables,
    GetMasterProductsQuery,
    GetMasterProductsQueryVariables,
    ProductQueryResult,
} from "@graphql/generated/types";
import { EccoGetMyActiveWishlistDocument, GetMasterProductsDocument } from "@graphql/generated/sdk";
import { useLocale } from "next-intl";

import { useCommerceToolsClient } from "@lib/commerce/adapters/commercetools/useCommerceToolsClient";
import { updateMutation, updateQuery } from "@store/helpers";
import { useStore } from "@store/store";

export const useCreateMyWishlist = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.createMyWishlist;
    const setData = useStore((state) => state.setWishlist);
    return updateMutation<CreateMyWishlistMutationVariables>(executeWithVariables, setData);
};

export const useDeleteMyWishlist = () => {
    const { client } = useCommerceToolsClient();
    const execute = client.deleteMyWishlist;
    const setData = useStore((state) => state.setWishlist);
    return updateMutation<DeleteMyWishlistMutationVariables>(execute, setData);
};

export const useGetMyActiveShoppingLists = () => {
    const { baseClient } = useCommerceToolsClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setWishlist);

    const selector = (data: any) => {
        return data.me.shoppingLists.results;
    };

    return updateQuery<EccoGetMyActiveWishlistQueryVariables, EccoGetMyActiveWishlistQuery>(
        baseClient,
        setData,
        selector,
        EccoGetMyActiveWishlistDocument,
        {
            locale: locale,
        }
    );
};

export const useGetMyActiveShoppingList = () => {
    const { baseClient } = useCommerceToolsClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setWishlist);

    const selector = (data: any) => {
        return data.me.shoppingLists.results[0];
    };

    return updateQuery<EccoGetMyActiveWishlistQueryVariables, EccoGetMyActiveWishlistQuery>(
        baseClient,
        setData,
        selector,
        EccoGetMyActiveWishlistDocument,
        {
            locale: locale,
        }
    );
};

export const useGetMasterDataForShoppingListLineItems = () => {
    const { baseClient } = useCommerceToolsClient();
    const setData = useStore((state) => state.setWishlistMasterProducts);

    const selector = (data: { products: ProductQueryResult }) => {
        const map = new Map();
        data.products?.results?.forEach((item) => {
            if (!map.has(item.id)) map.set(item.id, item.masterData.current);
        });

        return map;
    };

    const filter = {
        skus: "$skus",
        channelIds: process.env.NEXT_PUBLIC_CHANNEL_ID,
    };

    return updateQuery<GetMasterProductsQueryVariables, GetMasterProductsQuery>(
        baseClient,
        setData,
        selector,
        GetMasterProductsDocument,
        filter
    );
};
